"use client";

import { useEffect } from "react";
import Link from "next/link";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/Button";

/**
 * Error boundary global do app. Captura qualquer erro de render/data fetch
 * em todas as rotas (`/app/*`, `/admin/*`, etc.) que não tenham um boundary
 * mais próximo.
 *
 * Mostra UI amigável + botões "Tentar novamente" e "Voltar pro dashboard".
 * Loga no console pra debug. Em produção, idealmente reportar pra Sentry.
 */
export default function ErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[error-boundary]", error);
    // Reporta pra logger interno (persiste em ErrorLog)
    fetch("/api/errors", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        message: error.message ?? "unknown",
        stack: error.stack ?? null,
        digest: error.digest ?? null,
        path: typeof window !== "undefined" ? window.location.pathname : null,
      }),
      keepalive: true,
    }).catch(() => {
      /* swallow — não pode quebrar a UI */
    });
  }, [error]);

  const isAuth =
    /unauthor|forbidden|403|401|sign.?in/i.test(error.message ?? "") ||
    error.message?.includes("requireAccountAccess");

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="rounded-xl border border-red-200 bg-red-50/40 p-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-9 w-9 rounded-lg bg-red-100 text-red-700 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <h1 className="text-base font-semibold text-red-900">
              {isAuth ? "Sessão expirada ou sem permissão" : "Algo deu errado"}
            </h1>
          </div>

          <p className="text-sm text-red-800/90 mb-1">
            {isAuth
              ? "Faça login novamente pra continuar."
              : "Tivemos um problema ao carregar essa página. Já registramos o erro."}
          </p>

          {process.env.NODE_ENV !== "production" && (
            <pre className="mt-3 text-[10px] bg-white border border-red-200 rounded p-2 max-h-40 overflow-auto text-red-900 font-mono whitespace-pre-wrap break-words">
              {error.message}
              {error.digest ? `\n\ndigest: ${error.digest}` : ""}
            </pre>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {isAuth ? (
              <Link href="/login">
                <Button variant="primary" size="sm">
                  Ir pro login
                </Button>
              </Link>
            ) : (
              <Button
                variant="primary"
                size="sm"
                icon={<RefreshCw className="h-3 w-3" />}
                onClick={() => reset()}
              >
                Tentar novamente
              </Button>
            )}
            <Link href="/app">
              <Button
                variant="secondary"
                size="sm"
                icon={<Home className="h-3 w-3" />}
              >
                Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
